import type { Product } from '~/types/search';
import type { MultiSearchRequest, MultiSearchResponse, ParamValue, Scalar, SearchParams, SearchParamsWithPreset, SearchResponse } from '~/types/typesense';

/**
 * Analytics events emitted by catalog-related interactions.
 */
declare module '~/plugins/bus' {
  export interface Events {
    'catalog:add': (product: Product, collection?: string) => void;
    'catalog:search': (term: string) => void;
    'catalog:select': (product: Product, collection?: string) => void;
    'catalog:view': (products: Product[], collection?: string) => void;
  }
}

/**
 * Flat query-parameter map accepted by `$fetch`.
 *
 * Arrays are converted to comma-separated strings before request dispatch.
 */
type NormalizedParams = Record<string, Scalar | undefined>;

/**
 * Optional field projection options supported when fetching a document by id.
 */
type GetDocumentParams = Pick<SearchParams, 'include_fields' | 'exclude_fields'>;

/**
 * Executes one or more Typesense searches in a single HTTP request.
 *
 * Multi-search preserves the order of the supplied searches. Each entry in
 * `results` corresponds to the search at the same index in `searches`.
 *
 * The overloads support two common usage patterns:
 *
 * - A dynamic number of searches that all return the same document type.
 * - A fixed tuple of searches where each search may return a different
 *   document type.
 *
 * Search parameters are normalized before dispatch so array values such as
 * `query_by`, `facet_by`, and `include_fields` are serialized as
 * comma-separated strings expected by Typesense.
 */
interface MultiSearch {
  /**
   * Executes a fixed tuple of searches whose results may contain different
   * document types.
   *
   * Use this overload when the searches are known statically and preserving
   * the document type associated with each result index is useful.
   *
   * @typeParam T - Tuple containing the document type returned by each search.
   * @param searches - Tuple of search requests corresponding positionally to
   * the document types in `T`.
   * @returns A multi-search response whose result types correspond to the
   * supplied tuple.
   *
   * @example
   * const response = await multi<[Product, Category]>([
   *   {
   *     collection: 'products',
   *     q: 'glock',
   *     query_by: ['name', 'sku'],
   *   },
   *   {
   *     collection: 'categories',
   *     q: 'handguns',
   *     query_by: ['name'],
   *   },
   * ]);
   *
   * const productResults = response.results[0];
   * const categoryResults = response.results[1];
   */
  <T extends unknown[]>(searches: {
    readonly [Index in keyof T]: MultiSearchRequest;
  }): Promise<MultiSearchResponse<T>>;

  /**
   * Executes a dynamic collection of searches that all return the same
   * document type.
   *
   * Use this overload when the number of searches is determined at runtime,
   * such as disjunctive facet searches.
   *
   * @typeParam T - Document type returned by every search.
   * @param searches - Search requests to execute.
   * @returns A multi-search response containing one `SearchResponse<T>` for
   * each supplied search.
   *
   * @example
   * const searches: MultiSearchRequest[] = buildFacetSearches();
   *
   * const response = await multi<Product>(searches);
   *
   * for (const result of response.results) {
   *   // result is SearchResponse<Product>
   * }
   */
  <T>(searches: readonly MultiSearchRequest[]): Promise<MultiSearchResponse<T[]>>;
}

/**
 * Checks whether a request parameter value is a scalar array.
 *
 * @param value - Candidate query parameter value.
 * @returns `true` when the value is an array of scalar values.
 *
 * @example
 * isParamArray(['name', 'price']) // true
 */
const isParamArray = (value: ParamValue): value is readonly Scalar[] =>
  Array.isArray(value);

/**
 * Normalizes search parameters into a query-string friendly object.
 *
 * Undefined values are dropped and scalar arrays are joined with commas.
 *
 * @param params - Search params to normalize.
 * @returns A normalized record safe to pass to `$fetch` as `params`.
 *
 * @example
 * normalizeParams({ include_fields: ['id', 'name'], page: 1 })
 * // { include_fields: 'id,name', page: 1 }
 */
const normalizeParams = (params?: Record<string, ParamValue>): NormalizedParams => {
  const normalized: NormalizedParams = {};

  if (!params) {
    return normalized;
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }

    normalized[key] = isParamArray(value) ? value.join(',') : value;
  }

  return normalized;
};

export default defineNuxtPlugin({
  name: 'kygunco:typesense',
  dependsOn: [],
  parallel: true,
  order: 0,
  setup: () => {
    const config = useRuntimeConfig();

    const fetch = $fetch.create({
      baseURL: config.public.typesense.url ?? 'https://search.keenesdepot.io',
      onRequest: async ({ options }) => {
        const headers = new Headers(options.headers);
        headers.set('x-typesense-api-key', config.public.typesense.apiKey);
        options.headers = headers;
      },
    });

    /**
     * Executes a Typesense search request for a collection.
     *
     * @param collection - Target Typesense collection name.
     * @param params - Search parameters or preset-driven search parameters.
     * @returns Typesense search response payload for document type `T`.
     *
     * @example
     * const response = await search<Product>('products', {
     *   q: 'glock',
     *   query_by: ['name', 'sku'],
     * });
     */
    const search = <T>(
      collection: string,
      params: SearchParams | SearchParamsWithPreset) =>
      fetch<SearchResponse<T>>(`collections/${collection}/documents/search`, {
        params: normalizeParams(params),
      });

    /**
     * Executes Typesense searches through the `multi_search` endpoint.
     *
     * Each request is normalized before being included in the request body so
     * parameter arrays use Typesense's comma-separated wire format.
     */
    const multi: MultiSearch = (searches: readonly MultiSearchRequest[]) =>
      fetch<MultiSearchResponse<unknown[]>>('multi_search', {
        method: 'POST',
        body: {
          searches: searches.map(({ collection, ...searchParams }) => ({
            collection,
            ...normalizeParams(searchParams),
          })),
        },
      });

    /**
     * Returns the first matching document from a collection search.
     *
     * @param collection - Target Typesense collection name.
     * @param params - Search parameters used to find a matching document.
     * @param fail - When true, throws a 404 when no document is found.
     * @returns The first matched document or `undefined` when not found.
     * @throws {Error} Throws a Nuxt 404 error when `fail` is true and no hit exists.
     *
     * @example
     * const product = await first<Product>('products', {
     *   q: 'glock 19',
     *   query_by: 'name',
     * }, true);
     */
    const first = async <T>(
      collection: string,
      params: SearchParams | SearchParamsWithPreset,
      fail?: boolean) => {
      const response = await search<T>(collection, { ...params, limit: 1 });

      if (!response.hits?.[0]?.document && fail) {
        throw createError({ status: 404, statusMessage: 'Not Found' });
      }

      return response.hits?.[0]?.document;
    };

    /**
     * Fetches a single document by id from a Typesense collection.
     *
     * @param collection - Target Typesense collection name.
     * @param id - Document id to fetch.
     * @param params - Optional include/exclude field configuration.
     * @param fail - When true, rethrows fetch failures as Nuxt errors.
     * @returns Matched document when found, otherwise `null` if `fail` is falsy.
     * @throws {Error} Throws a Nuxt error when `fail` is true and fetch fails.
     *
     * @example
     * const product = await get<Product>('products', '12345', {
     *   include_fields: ['id', 'name', 'price'],
     * });
     */
    const get = async <T>(
      collection: string,
      id: string,
      params?: GetDocumentParams,
      fail?: boolean) => {
      try {
        return await fetch<T>(`collections/${collection}/documents/${id}`, {
          params: params !== undefined ? normalizeParams(params) : undefined,
        });
      }
      catch (error) {
        if (fail) {
          if (isFetchError(error)) {
            throw createError({ status: error.status, statusMessage: error.statusText });
          }

          throw createError('An unknown error occurred while fetching the document.');
        }

        return null;
      }
    };

    return {
      provide: {
        typesense: {
          fetch,
          search,
          multi,
          first,
          get,
        },
      },
    };
  },
});
